import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, ChevronRight, Cloud, Download, Globe, Layers, RefreshCw, Search, ShieldAlert, Sparkles, Upload } from "lucide-react";

const ENV_MODE = "preview"; // frontend badge — backend also returns the real value

const STATUS_LABEL = {
  new: "Nuovo prodotto",
  existing_unchanged: "Prodotto esistente",
  title_changed: "Titolo modificato",
  brand_changed: "Marchio modificato",
  brand_and_title_changed: "Prodotto da aggiornare",
  duplicate_code: "Codice duplicato",
  missing_code: "Codice mancante",
  missing_title: "Titolo mancante",
  invalid_page: "Pagina non valida",
};
const BRAND_LABEL = {
  matched: "Marchio associato",
  alias_matched: "Alias marchio",
  manual: "Marchio manuale",
  to_verify: "Marchio da verificare",
  conflict: "Marchio in conflitto",
  not_found: "Marchio non trovato",
  absent: "Marchio assente",
};
const ACTION_LABEL = {
  create: "Crea prodotto",
  update_title: "Aggiorna titolo",
  update_brand: "Aggiorna marchio",
  update_both: "Aggiorna titolo e marchio",
  keep: "Mantieni",
  skip: "Ignora",
  review: "Rivedi",
};

const STATUS_COLOR = {
  new: "bg-emerald-100 text-emerald-700",
  existing_unchanged: "bg-slate-100 text-slate-600",
  title_changed: "bg-amber-100 text-amber-700",
  brand_changed: "bg-amber-100 text-amber-700",
  brand_and_title_changed: "bg-amber-100 text-amber-700",
  duplicate_code: "bg-rose-100 text-rose-700",
  missing_code: "bg-rose-100 text-rose-700",
  missing_title: "bg-rose-100 text-rose-700",
  invalid_page: "bg-slate-100 text-slate-600",
};

const FILTERS = [
  { key: "all", label: "Tutti" },
  { key: "new", label: "Nuovi" },
  { key: "existing", label: "Esistenti" },
  { key: "to_update", label: "Da aggiornare" },
  { key: "brand_ok", label: "Marchio associato" },
  { key: "brand_verify", label: "Marchio da verificare" },
  { key: "duplicates", label: "Duplicati" },
  { key: "invalid", label: "Non validi" },
];

export default function CatalogImportWebsite({ mode = "website" }) {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(0); // 0 config, 1 running, 2 preview, 3 simulated, 4 report
  const [sessionId, setSessionId] = useState(null);
  const [entries, setEntries] = useState([]);
  const [meta, setMeta] = useState({});
  const [manufacturers, setManufacturers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [simStats, setSimStats] = useState(null);
  const [reportBatch, setReportBatch] = useState(null);
  const [busy, setBusy] = useState(false);

  // Website options
  const [startUrl, setStartUrl] = useState("https://www.amasicurezza.com/");
  const [maxPages, setMaxPages] = useState(200);
  const [maxDepth, setMaxDepth] = useState(4);

  // File options
  const [fileObj, setFileObj] = useState(null);
  const [delimiter, setDelimiter] = useState(";");

  useEffect(() => { api.get("/manufacturers").then((r) => setManufacturers(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const sid = params.get("session");
    if (sid) { loadSession(sid); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSession = async (sid) => {
    try {
      const r = await api.get(`/catalog/sessions/${sid}`);
      setSessionId(sid); setEntries(r.data.entries || []); setMeta(r.data);
      setStep(2);
    } catch { toast.error("Sessione non trovata o scaduta"); }
  };

  const analyze = async () => {
    setBusy(true); setStep(1);
    try {
      if (mode === "file") {
        if (!fileObj) throw new Error("Nessun file selezionato");
        const fd = new FormData();
        fd.append("file", fileObj);
        fd.append("delimiter", delimiter);
        const r = await api.post("/catalog/file-analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setSessionId(r.data.session_id);
        setEntries(r.data.entries || []);
        setMeta(r.data);
        setStep(2);
        toast.success(`Analisi completata: ${r.data.products_count} prodotti trovati`);
        return;
      }
      // Website mode: preflight → job → poll
      const pf = await api.post("/catalog/preflight", { start_url: startUrl }).catch((e) => ({ __err: e }));
      if (pf.__err || !pf.data?.success) {
        const detail = pf.data || {};
        const msg = detail.message || "Il server non riesce a collegarsi al sito AMA Sicurezza.";
        toast.error(msg);
        setMeta({ preflight: detail, environment: detail.environment });
        setStep(0);
        return;
      }
      const job = await api.post("/catalog/analyze", { start_url: startUrl, max_pages: maxPages, max_depth: maxDepth });
      const jobId = job.data.job_id;
      // Poll every 2s up to 5 minutes
      const deadline = Date.now() + 5 * 60 * 1000;
      let last = null;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 2000));
        const s = await api.get(`/catalog/jobs/${jobId}`);
        last = s.data;
        setMeta({ ...last, preflight: pf.data });
        if (last.status === "completed") { break; }
        if (last.status === "failed") { break; }
      }
      if (!last || last.status === "failed") {
        const errCode = last?.error_code || "UNKNOWN";
        toast.error(`L'analisi non è stata avviata a causa di un errore del backend. (${errCode})`);
        setStep(0);
        return;
      }
      if (last.status !== "completed") {
        toast.error("L'analisi è ancora in corso. Riprova più tardi.");
        setStep(0);
        return;
      }
      setSessionId(last.session_id);
      setEntries(last.entries || []);
      setStep(2);
      toast.success(`Analisi completata: ${last.products_count || 0} prodotti trovati`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.message || e.message || "Impossibile completare l'analisi.";
      toast.error(msg);
      setStep(0);
    } finally { setBusy(false); }
  };

  const patchEntry = async (row, patch) => {
    const next = entries.map((e) => e.row_id === row.row_id ? { ...e, ...patch } : e);
    setEntries(next);
    try {
      await api.patch(`/catalog/sessions/${sessionId}/entries/${row.row_id}`, patch);
    } catch { toast.error("Errore salvataggio modifica"); }
  };

  const simulate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/catalog/sessions/${sessionId}/simulate`);
      setSimStats(r.data.stats);
      setStep(3);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore simulazione"); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    if (!window.confirm(`Confermi l'importazione definitiva di ${simStats?.new || 0} nuovi prodotti + ${simStats?.to_update || 0} aggiornamenti?`)) return;
    setBusy(true);
    try {
      const key = `${sessionId}-${Date.now()}`;
      const r = await api.post(`/catalog/sessions/${sessionId}/confirm`, { idempotency_key: key });
      setReportBatch(r.data);
      setStep(4);
      toast.success("Importazione completata.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Importazione non completata."); }
    finally { setBusy(false); }
  };

  const cancel = () => {
    if (!window.confirm("Annullare la sessione di importazione? I dati temporanei verranno scartati.")) return;
    setSessionId(null); setEntries([]); setSimStats(null); setStep(0);
  };

  const filtered = useMemo(() => {
    let list = entries;
    if (filter === "new") list = list.filter((e) => e.product_status === "new");
    else if (filter === "existing") list = list.filter((e) => e.product_status === "existing_unchanged");
    else if (filter === "to_update") list = list.filter((e) => ["title_changed", "brand_changed", "brand_and_title_changed"].includes(e.product_status));
    else if (filter === "brand_ok") list = list.filter((e) => ["matched", "alias_matched"].includes(e.brand_status));
    else if (filter === "brand_verify") list = list.filter((e) => ["to_verify", "conflict", "not_found", "absent"].includes(e.brand_status));
    else if (filter === "duplicates") list = list.filter((e) => e.product_status === "duplicate_code");
    else if (filter === "invalid") list = list.filter((e) => ["missing_code", "missing_title", "invalid_page"].includes(e.product_status));
    if (q) {
      const s = q.toLowerCase();
      list = list.filter((e) => (e.product_code + " " + e.product_title + " " + (e.source_brand_name || "") + " " + (e.source_product_url || "")).toLowerCase().includes(s));
    }
    return list;
  }, [entries, filter, q]);

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-4" data-testid="catalog-import-page">
      <Link to="/catalogo" className="text-sm text-slate-500 inline-flex items-center gap-1 hover:text-[#d63384]"><ArrowLeft className="w-4 h-4" /> Catalogo prodotti</Link>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#d63384] font-semibold">Catalogo prodotti</div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-[#0a0a2e]">
            {mode === "website" ? "Importazione dal sito AMA" : "Importazione da file"}
          </h1>
          {mode === "website" && <p className="text-sm text-slate-500 flex items-center gap-1"><Globe className="w-3.5 h-3.5" /> Sito AMA Sicurezza — <span className="font-mono">www.amasicurezza.com</span></p>}
        </div>
        <Badge className={`${(meta.environment || ENV_MODE) === "production" ? "bg-emerald-600" : "bg-amber-500"} text-white`}>{(meta.environment || ENV_MODE) === "production" ? "AMBIENTE DI PRODUZIONE" : "AMBIENTE DI ANTEPRIMA"}</Badge>
      </div>

      {(meta.environment || ENV_MODE) !== "production" && (
        <Alert className="bg-amber-50 border-amber-200 text-amber-800">
          <AlertTitle>Ambiente di anteprima</AlertTitle>
          <AlertDescription>Utilizzare esclusivamente dati di prova. Eseguire l'importazione definitiva nell'ambiente di produzione dopo aver completato tutti i controlli.</AlertDescription>
        </Alert>
      )}

      {/* Stepper */}
      <div className="flex items-center gap-2 text-xs text-slate-600 flex-wrap">
        {[
          { n: "Configura", i: 0 }, { n: "Anteprima", i: 2 }, { n: "Simulazione", i: 3 }, { n: "Rapporto", i: 4 },
        ].map((s, idx, arr) => (
          <div key={s.n} className="flex items-center gap-2">
            <div className={`px-3 py-1 rounded-full font-semibold ${step >= s.i ? "bg-[#0a0a2e] text-white" : "bg-slate-100"}`}>{idx + 1}. {s.n}</div>
            {idx < arr.length - 1 && <ChevronRight className="w-3 h-3 text-slate-400" />}
          </div>
        ))}
      </div>

      {/* STEP 0 — config */}
      {step === 0 && (
        <Card className="ama-card border-0 shadow-none">
          <CardHeader><CardTitle className="font-heading text-lg">{mode === "website" ? "Analizza il sito" : "Carica il file"}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {mode === "website" ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="md:col-span-3"><Label>URL sorgente</Label><Input data-testid="input-start-url" value={startUrl} onChange={(e) => setStartUrl(e.target.value)} /></div>
                  <div><Label>Pagine massime</Label><Input type="number" value={maxPages} onChange={(e) => setMaxPages(+e.target.value)} /></div>
                  <div><Label>Profondità max</Label><Input type="number" value={maxDepth} onChange={(e) => setMaxDepth(+e.target.value)} /></div>
                </div>
                <p className="text-xs text-slate-500">Il crawler segue solo link su www.amasicurezza.com, esclude immagini/PDF/aree riservate e rispetta i limiti di frequenza. Nessuna immagine viene scaricata o memorizzata.</p>
              </>
            ) : (
              <>
                <div><Label>File (CSV ; / , • TXT tab • XLSX)</Label>
                  <Input data-testid="input-file" type="file" accept=".csv,.txt,.xlsx" onChange={(e) => setFileObj(e.target.files?.[0] || null)} />
                </div>
                <div><Label>Delimitatore CSV</Label>
                  <select value={delimiter} onChange={(e) => setDelimiter(e.target.value)} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                    <option value=";">Punto e virgola (;)</option>
                    <option value=",">Virgola (,)</option>
                    <option value="auto">Rilevamento automatico</option>
                  </select>
                </div>
                <div className="text-xs text-slate-500">Colonne obbligatorie: <b>Codice prodotto</b>, <b>Titolo prodotto</b>. Opzionali: Marchio, URL sorgente.</div>
              </>
            )}
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              {mode === "website" && (
                <Button data-testid="btn-preflight" variant="outline" onClick={async () => {
                  try {
                    const r = await api.post("/catalog/preflight", { start_url: startUrl });
                    setMeta({ preflight: r.data });
                    if (r.data.success) toast.success("Collegamento al sito AMA riuscito.");
                    else toast.error(r.data.message || "Collegamento non riuscito.");
                  } catch (e) { toast.error(e?.response?.data?.detail || "Errore durante il test."); }
                }}><Globe className="w-4 h-4 mr-1" /> Testa collegamento al sito AMA</Button>
              )}
              <Button data-testid="btn-analyze" onClick={analyze} disabled={busy || (mode === "file" && !fileObj)} className="ama-btn-primary">
                {busy ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Analisi in corso…</> : <><Sparkles className="w-4 h-4 mr-1" /> {mode === "website" ? "Analizza sito" : "Analizza file"}</>}
              </Button>
            </div>
            {meta.preflight && (
              <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm space-y-1" data-testid="preflight-summary">
                <div className="font-semibold text-[#0a0a2e] flex items-center gap-2">Diagnostica collegamento
                  {meta.preflight.success ? <Badge className="bg-emerald-600 text-white">Riuscito</Badge> : <Badge className="bg-rose-600 text-white">Non riuscito</Badge>}
                </div>
                <PfRow k="DNS" v={meta.preflight.dns?.ok ? `riuscito (${(meta.preflight.dns?.addresses||[]).join(", ")})` : "non riuscito"} ok={meta.preflight.dns?.ok} />
                <PfRow k="HTTPS/TLS" v={meta.preflight.tls?.ok ? "riuscito" : "non riuscito"} ok={meta.preflight.tls?.ok} />
                <PfRow k="Homepage" v={`status ${meta.preflight.homepage?.http_status ?? "-"} · ${meta.preflight.homepage?.elapsed_ms ?? "-"} ms`} ok={meta.preflight.homepage?.ok} />
                <PfRow k="Sitemap" v={meta.preflight.sitemap?.ok ? `trovata (${meta.preflight.sitemap?.url_count} URL)` : "non trovata"} ok={meta.preflight.sitemap?.ok} />
                <PfRow k="Pagina prodotto" v={meta.preflight.product_page?.url ? (meta.preflight.product_page?.ok ? `raggiungibile · codice: ${meta.preflight.product_page?.detected?.product_code || "-"}` : `non raggiungibile (${meta.preflight.product_page?.http_status ?? "-"})`) : "non individuata"} ok={!!meta.preflight.product_page?.ok} />
                {meta.preflight.error_code && <div className="text-xs text-rose-600">Codice: {meta.preflight.error_code}{meta.preflight.message ? ` — ${meta.preflight.message}` : ""}</div>}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* STEP 1 running */}
      {step === 1 && (
        <Card className="ama-card border-0 shadow-none">
          <CardContent className="p-10 text-center">
            <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin text-[#d63384]" />
            <div className="font-heading font-semibold">
              {meta.status === "completed" ? "Elaborazione risultati…" :
                meta.phase === "queued" ? "Job in coda…" :
                meta.phase === "crawling" ? "Analisi delle pagine in corso…" :
                meta.phase === "enriching" ? "Confronto con il catalogo…" :
                "Analisi in corso…"}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              L'operazione può richiedere qualche minuto. Puoi lasciare aperta questa pagina.
              {meta.job_id && <div className="mt-1 font-mono">Job: {meta.job_id.slice(0, 8)}</div>}
              {meta.pages_analysed != null && <div className="mt-1">Pagine analizzate finora: {meta.pages_analysed}</div>}
            </div>
            {(meta.error_code || meta.preflight?.error_code) && (
              <details className="mt-4 text-left text-xs text-slate-500 max-w-md mx-auto" data-testid="running-tech-details">
                <summary className="cursor-pointer">Dettagli tecnici</summary>
                <div className="mt-1 font-mono">
                  <div>Codice: {meta.error_code || meta.preflight?.error_code || "-"}</div>
                  <div>HTTP: {meta.http_status || meta.preflight?.homepage?.http_status || "-"}</div>
                  <div>Job: {meta.job_id || "-"}</div>
                  <div>Retryable: sì</div>
                </div>
              </details>
            )}
          </CardContent>
        </Card>
      )}

      {/* STEP 2 preview */}
      {step === 2 && (
        <>
          <Card className="ama-card border-0 shadow-none">
            <CardContent className="p-4 text-sm grid grid-cols-2 md:grid-cols-4 gap-2">
              <Stat k="Pagine analizzate" v={meta.pages_analysed} />
              <Stat k="Prodotti trovati" v={meta.products_count ?? entries.length} />
              <Stat k="Errori pagina" v={meta.errors_count ?? 0} />
              <Stat k="Sitemap URL" v={meta.sitemap_urls ?? "—"} />
            </CardContent>
          </Card>

          <div className="flex flex-wrap gap-2 items-center">
            {FILTERS.map((f) => (
              <button key={f.key} data-testid={`preview-filter-${f.key}`} onClick={() => setFilter(f.key)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${filter === f.key ? "bg-[#0a0a2e] text-white border-transparent" : "bg-white text-slate-700 border-slate-200"}`}>{f.label}</button>
            ))}
            <div className="relative ml-auto">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="preview-search" className="pl-9 w-56" placeholder="Cerca…" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
          </div>

          <div className="hidden md:block overflow-x-auto ama-card">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <tr>
                  <th className="text-left py-2 px-3">Codice</th>
                  <th className="text-left py-2 px-3">Titolo</th>
                  <th className="text-left py-2 px-3">Marchio rilevato</th>
                  <th className="text-left py-2 px-3">Produttore AMA</th>
                  <th className="text-left py-2 px-3">Stato prodotto</th>
                  <th className="text-left py-2 px-3">Stato marchio</th>
                  <th className="text-left py-2 px-3">Azione</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => <PreviewRow key={e.row_id} row={e} manufacturers={manufacturers} onPatch={(p) => patchEntry(e, p)} />)}
              </tbody>
            </table>
            {filtered.length === 0 && <div className="p-8 text-center text-slate-500 text-sm">Nessun risultato per i filtri attivi.</div>}
          </div>

          <div className="md:hidden space-y-2" data-testid="preview-mobile-list">
            {filtered.map((e) => <MobileCard key={e.row_id} row={e} manufacturers={manufacturers} onPatch={(p) => patchEntry(e, p)} />)}
          </div>

          <div className="flex flex-wrap justify-end gap-2 pt-2">
            <Button variant="outline" onClick={cancel}>Annulla</Button>
            <Button data-testid="btn-simulate" onClick={simulate} disabled={busy} className="ama-btn-primary"><Layers className="w-4 h-4 mr-1" /> Simula importazione</Button>
          </div>
        </>
      )}

      {/* STEP 3 simulation */}
      {step === 3 && simStats && (
        <Card className="ama-card border-0 shadow-none" data-testid="simulate-result-card">
          <CardHeader><CardTitle className="font-heading text-lg">Rapporto di simulazione</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            <Stat k="Totale voci" v={simStats.total} />
            <Stat k="Nuovi prodotti" v={simStats.new} />
            <Stat k="Prodotti da aggiornare" v={simStats.to_update} />
            <Stat k="Esistenti invariati" v={simStats.existing_unchanged} />
            <Stat k="Ignorati" v={simStats.skipped} />
            <Stat k="Codici duplicati" v={simStats.duplicate_code} />
            <Stat k="Codici mancanti" v={simStats.missing_code} />
            <Stat k="Titoli mancanti" v={simStats.missing_title} />
            <Stat k="Marchio associato" v={simStats.brands_auto_matched} />
            <Stat k="Marchio via alias" v={simStats.brands_alias_matched} />
            <Stat k="Marchio manuale" v={simStats.brands_manual} />
            <Stat k="Marchio da verificare" v={simStats.brands_to_verify + simStats.brands_absent} />
          </CardContent>
          <CardContent className="flex flex-wrap justify-end gap-2 pt-0">
            <Button variant="outline" onClick={() => setStep(2)}>Torna all'anteprima</Button>
            <Button variant="outline" onClick={cancel}>Annulla</Button>
            <Button data-testid="btn-confirm" onClick={confirm} disabled={busy} className="ama-btn-primary"><CheckCircle2 className="w-4 h-4 mr-1" /> Conferma importazione</Button>
          </CardContent>
        </Card>
      )}

      {/* STEP 4 report */}
      {step === 4 && reportBatch && (
        <Card className="ama-card border-0 shadow-none" data-testid="import-report-card">
          <CardHeader><CardTitle className="font-heading text-lg">Importazione completata</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Stat k="Creati" v={reportBatch.counts?.created || 0} />
              <Stat k="Aggiornati" v={reportBatch.counts?.updated || 0} />
              <Stat k="Invariati" v={reportBatch.counts?.unchanged || 0} />
              <Stat k="Ignorati" v={reportBatch.counts?.skipped || 0} />
              <Stat k="Falliti" v={reportBatch.counts?.failed || 0} />
              <Stat k="Batch" v={<span className="font-mono text-xs">{reportBatch.id?.slice(0, 8)}</span>} />
            </div>
            <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
              <Button data-testid="btn-download-csv" variant="outline" onClick={() => {
                const url = `${api.defaults.baseURL}/catalog/history/${reportBatch.id}/report.csv`;
                api.get(`/catalog/history/${reportBatch.id}/report.csv`, { responseType: "blob" }).then((r) => {
                  const blob = new Blob([r.data], { type: "text/csv" });
                  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `import_${reportBatch.id.slice(0,8)}.csv`; a.click();
                });
              }}><Download className="w-4 h-4 mr-1" /> Scarica rapporto CSV</Button>
              <Link to="/catalogo/storico"><Button variant="outline">Vai allo storico</Button></Link>
              <Button onClick={() => nav("/catalogo")} className="ama-btn-primary">Torna al catalogo</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div className="p-3 rounded-lg bg-white border border-slate-100">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{k}</div>
      <div className="text-lg font-heading font-bold text-[#0a0a2e]">{v}</div>
    </div>
  );
}

function PfRow({ k, v, ok }) {
  return (
    <div className="flex justify-between gap-2 text-xs">
      <span className="text-slate-600">{k}</span>
      <span className={`font-mono ${ok ? "text-emerald-700" : "text-rose-700"}`}>{v}</span>
    </div>
  );
}

function StatusBadge({ row }) {
  const s = row.product_status;
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_COLOR[s] || "bg-slate-100 text-slate-600"}`}>{STATUS_LABEL[s] || s}</span>;
}
function BrandBadge({ row }) {
  return <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-700">{BRAND_LABEL[row.brand_status] || row.brand_status}</span>;
}

function PreviewRow({ row, manufacturers, onPatch }) {
  return (
    <tr className="border-t border-slate-100" data-testid={`preview-row-${row.row_id}`}>
      <td className="py-2 px-3 font-mono text-xs">{row.product_code || <em className="text-rose-500">mancante</em>}</td>
      <td className="py-2 px-3">{row.product_title || <em className="text-rose-500">mancante</em>}</td>
      <td className="py-2 px-3 text-xs text-slate-600">{row.source_brand_name || "—"}</td>
      <td className="py-2 px-3">
        <select value={row.manufacturer_id || ""} onChange={(e) => {
          const m = manufacturers.find((x) => x.id === e.target.value);
          onPatch({ manufacturer_id: e.target.value, manufacturer_name: m ? m.name : "", brand_status: e.target.value ? "manual" : row.brand_status });
        }} className="text-xs border border-slate-200 rounded px-2 py-1 bg-white max-w-[160px]">
          <option value="">—</option>
          {manufacturers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      </td>
      <td className="py-2 px-3"><StatusBadge row={row} /></td>
      <td className="py-2 px-3"><BrandBadge row={row} /></td>
      <td className="py-2 px-3">
        <select value={row.action} onChange={(e) => onPatch({ action: e.target.value })} className="text-xs border border-slate-200 rounded px-2 py-1 bg-white">
          {Object.entries(ACTION_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </td>
    </tr>
  );
}

function MobileCard({ row, manufacturers, onPatch }) {
  return (
    <div className="ama-card p-3 space-y-1" data-testid={`preview-mob-${row.row_id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="font-mono text-sm font-bold">{row.product_code || <em className="text-rose-500">codice mancante</em>}</div>
        <StatusBadge row={row} />
        <BrandBadge row={row} />
      </div>
      <div className="text-sm text-[#0a0a2e]">{row.product_title || <em className="text-rose-500">titolo mancante</em>}</div>
      <div className="text-[11px] text-slate-500">Marchio rilevato: {row.source_brand_name || "—"}</div>
      <div className="grid grid-cols-2 gap-2 pt-1">
        <select value={row.manufacturer_id || ""} onChange={(e) => {
          const m = manufacturers.find((x) => x.id === e.target.value);
          onPatch({ manufacturer_id: e.target.value, manufacturer_name: m ? m.name : "", brand_status: e.target.value ? "manual" : row.brand_status });
        }} className="text-xs border border-slate-200 rounded px-2 py-1 bg-white">
          <option value="">Produttore —</option>
          {manufacturers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
        <select value={row.action} onChange={(e) => onPatch({ action: e.target.value })} className="text-xs border border-slate-200 rounded px-2 py-1 bg-white">
          {Object.entries(ACTION_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
    </div>
  );
}
